import { Skeleton } from '@/components/ui';

interface Props {
  /** Show the express-checkout placeholder row above the cards. */
  showExpressCheckout?: boolean;
  /** Number of saved-card rows to mock. */
  rows?: number;
}

/**
 * Loading state for the entire checkout body — skeletons that match the
 * shape of {@link PaymentMethodCard}, the express-checkout buttons, and
 * the primary CTA. Used while the Pay.com SDK is initialising and while
 * `getExistingSources()` is resolving (Phase 0 fix).
 */
export function PaymentMethodsSkeleton({ showExpressCheckout = true, rows = 2 }: Props) {
  return (
    <div className="flex flex-col gap-4" aria-busy="true" aria-live="polite">
      {/* order summary placeholder */}
      <div className="rounded-card border border-border bg-surface p-4 flex flex-col gap-2">
        <Skeleton className="h-3 w-20 rounded" />
        <Skeleton className="h-7 w-32 rounded" />
        <Skeleton className="h-3 w-40 rounded" />
      </div>

      {showExpressCheckout && (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-12 w-full rounded-card" />
          <div className="flex items-center gap-3 px-1">
            <Skeleton className="h-px flex-1 rounded" />
            <Skeleton className="h-3 w-12 rounded" />
            <Skeleton className="h-px flex-1 rounded" />
          </div>
        </div>
      )}

      {/* saved-card placeholders */}
      <div className="flex flex-col gap-2">
        <Skeleton className="h-3 w-24 rounded" />
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="flex items-center gap-3 rounded-card border border-border bg-surface px-4 py-3">
            <Skeleton className="h-5 w-9 rounded" />
            <Skeleton className="h-4 w-32 rounded" />
            <Skeleton className="ml-auto size-5 rounded-full" />
          </div>
        ))}
      </div>

      {/* CTA */}
      <Skeleton className="h-[52px] w-full rounded-tab" />
    </div>
  );
}
